import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import styled from "styled-components";
import { categoryHistory } from '../utils/categoryHistory';
import {
  FiArrowLeft,
  FiMessageCircle,
  FiHeart,
  FiShare2,
  FiArrowRight,
  FiShoppingCart,
  FiList,
  FiX,
  FiMinus,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { IconType } from 'react-icons';
import { useConsultation } from "../context/ConsultationContext";
import { useAuth } from "../context/AuthContext";
import { Product } from "../types";
import { IconContext } from "react-icons";
import { productService } from "../services/productService";
import { comboService, Combo } from "../services/comboService";
import { toast } from "react-toastify";
import { getImageUrl } from "../config";

// Helper function to render icons safely
const renderIcon = (IconComponent: IconType, props?: any) => {
  return React.createElement(IconComponent as any, props);
};



const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #3498db;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #ecf0f1;
  }
`;

const ProductContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 4rem;
  margin-bottom: 3rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const StickyImageWrapper = styled.div`
  position: sticky;
  top: 2rem;
  align-self: start;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: opacity 0.5s ease, transform 0.5s ease;
`;

const Badge = styled.span<{ type: "new" | "sale" | "featured" }>`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: ${(props) => {
    switch (props.type) {
      case "new":
        return "#2ecc71";
      case "sale":
        return "#e74c3c";
      case "featured":
        return "#FDBF37";
      default:
        return "#3498db";
    }
  }};
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 100%;
  overflow-x: hidden;
  padding-left: 1rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const ProductName = styled.h1`
  color: #2c3e50;
  font-size: 2.5rem;
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
`;

const ProductCategory = styled.span`
  color: #3498db;
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ProductDescription = styled.p`
  color: #7f8c8d;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
`;

const StockInfo = styled.div<{ inStock: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(props) => (props.inStock ? "#27ae60" : "#e74c3c")};
  font-weight: 600;
  font-size: 1rem;
`;

const PriceDisplay = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1e3a8a;
  margin: 1.5rem 0 1rem 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;

  .currency {
    font-size: 1.3rem;
    font-weight: 600;
    color: #4b5563;
  }

  .amount {
    font-size: 2.2rem;
    color: #1e3a8a;
  }
`;

const MainQuantitySection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0 1.5rem 0;
`;

const MainQuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #f8fafc;
  padding: 0.35rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const MainQuantityButton = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  color: #334155;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MainQuantityDisplay = styled.span`
  font-size: 1.15rem;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  color: #1e293b;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const MainAddToCartButton = styled.button<{ isOutOfStock?: boolean }>`
  flex: 1;
  background: ${(props) => (props.isOutOfStock ? "#9ca3af" : "#FDBF37")};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${(props) => (props.isOutOfStock ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: all 0.3s ease;
  box-shadow: ${(props) => (props.isOutOfStock ? "none" : "0 4px 14px rgba(253, 191, 55, 0.4)")};

  &:hover {
    ${(props) =>
      !props.isOutOfStock &&
      `
      background: #f59e0b;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(245, 158, 11, 0.45);
    `}
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #7f8c8d;
  border: 2px solid #ecf0f1;
  padding: 1rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #3498db;
    color: #3498db;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #7f8c8d;
`;

const NotFound = styled.div`
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
  font-size: 1.1rem;
`;

const GalleryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 10px;
  overflow: hidden;
  min-height: 400px;

  img {
    width: 100%;
    height: 400px;
    border-radius: 8px;
    object-fit: cover;
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 10px;
  padding: 5px 0;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
  }
`;

const Thumbnail = styled.div<{ active: boolean }>`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid ${(props) => (props.active ? "#4a90e2" : "transparent")};
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.prev {
    left: 10px;
  }

  &.next {
    right: 10px;
  }
`;

// Define IconWrapper component
const IconWrapper = styled.div<{ size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props) => props.size || 24}px;
  height: ${(props) => props.size || 24}px;
`;

const CombosSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 12px;
`;

const CombosTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CombosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
`;

const ComboCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ComboImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const ComboName = styled.h3`
  color: #2c3e50;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const ComboDescription = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const ComboProducts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ProductTag = styled.span`
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ComboMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: #7f8c8d;
`;

const NoComboMessage = styled.div`
  text-align: center;
  color: #7f8c8d;
  font-style: italic;
  padding: 2rem;
`;

// Nuevos estilos para la visualización de combos
const ComboDisplaySection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  max-width: 100%;
  overflow: hidden;
`;

const ComboDisplayTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
`;

const ComboDisplayCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ComboDisplayProducts = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const AddToCartSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0 0 0;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.8rem;
  }
`;

const ProductInCombo = styled.div<{ isMain: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 85px;
  height: 115px;
  padding: 0.3rem;
  border-radius: 6px;
  background: ${props => props.isMain ? '#e3f2fd' : '#f5f5f5'};
  border: ${props => props.isMain ? '2px solid #2196f3' : '1px solid #ddd'};
  position: relative;
  justify-content: space-between;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: ${props => props.isMain ? '0 3px 10px rgba(33, 150, 243, 0.2)' : '0 2px 6px rgba(0, 0, 0, 0.1)'};
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isMain ? '0 5px 15px rgba(33, 150, 243, 0.3)' : '0 3px 12px rgba(0, 0, 0, 0.15)'};
  }

  @media (max-width: 768px) {
    width: 75px;
    height: 105px;
    padding: 0.25rem;
  }
`;

const ComboProductImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 0.2rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const ComboProductName = styled.h3<{ isMain?: boolean }>`
  font-size: 0.5rem;
  margin: 0;
  color: #2c3e50;
  font-weight: ${(props) => props.isMain ? '600' : '500'};
  line-height: 1.1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 100%;
  height: 1.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;

  @media (max-width: 768px) {
    font-size: 0.45rem;
    height: 1.6em;
  }
`;

const PlusSymbol = styled.div`
  font-size: 1rem;
  font-weight: bold;
  color: #FDBF37;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0.3rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0.25rem;
  }
`;

const ComboDisplayMeta = styled.div`
  text-align: center;
  border-top: 1px solid #eee;
  padding-top: 1rem;
`;

const ComboDisplayName = styled.h4`
  color: #2c3e50;
  font-size: 1.1rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
`;

const ComboDisplayDescription = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
`;

const ProductCheckbox = styled.input`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2196f3;
`;

const AddToCartButton = styled.button`
  background: linear-gradient(135deg, #FDBF37, #FDBF37);
  color: white;
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin: 0;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.5rem 0.8rem;
  }
`;

const ProductContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  justify-content: center;
`;

const ConsultationIndicator = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #FDBF37, #FDBF37);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
  }
`;

const ViewListButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 30px;
  background: linear-gradient(135deg, #FDBF37, #FDBF37);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  z-index: 2;
  gap: 1px;

  &:hover {
    background: linear-gradient(135deg, #e55a2b, #e8851a);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  .e-letter {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
  }

  .e-line {
    width: 10px;
    height: 2px;
    background: white;
    border-radius: 1px;
  }

  .e-line.middle {
    width: 7px;
  }
`;

const ViewListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 85px;
  height: 115px;
  padding: 0.3rem;
  border-radius: 6px;
  background: #f0f8ff;
  border: 2px solid #FDBF37;
  position: relative;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover {
    background: #e6f3ff;
    border-color: #e55a2b;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.2);
  }

  @media (max-width: 768px) {
    width: 75px;
    height: 105px;
    padding: 0.25rem;
  }
`;

const ViewListIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background: linear-gradient(135deg, #FDBF37, #FDBF37);
  border-radius: 8px;
  margin-bottom: 0.4rem;
  box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);
  
  .e-letter {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .e-line {
    width: 12px;
    height: 2px;
    background: white;
    border-radius: 1px;
  }

  .e-line.middle {
    width: 9px;
  }

  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    margin-bottom: 0.3rem;
    
    .e-line {
      width: 10px;
      height: 1.5px;
    }
    
    .e-line.middle {
      width: 7px;
    }
  }
`;

const ViewListText = styled.span`
  font-size: 0.5rem;
  margin: 0;
  color: #FDBF37;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  max-width: 100%;
  height: auto;
  display: block;

  @media (max-width: 768px) {
    font-size: 0.45rem;
  }
`;

const SidePanel = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-400px'};
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  transition: right 0.3s ease;
  overflow-y: auto;
  padding: 2rem;

  @media (max-width: 768px) {
    width: 100vw;
    right: ${props => props.isOpen ? '0' : '-100vw'};
  }
`;

const SidePanelOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SidePanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #ecf0f1;
`;

const SidePanelTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: #ecf0f1;
    color: #2c3e50;
  }
`;

const ProductListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #ecf0f1;
  border-radius: 8px;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #3498db;
    box-shadow: 0 2px 8px rgba(52, 152, 219, 0.1);
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProductListImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const ProductListInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ProductListName = styled.h4`
  color: #2c3e50;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 600;
  line-height: 1.3;
`;

const ProductListCategory = styled.span`
  color: #7f8c8d;
  font-size: 0.8rem;
  font-weight: 500;
`;

const SidePanelCheckbox = styled.input`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #2196f3;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0;
`;



const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 2px solid #FDBF37;
  background: white;
  color: #FDBF37;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: #FDBF37;
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityDisplay = styled.div`
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  padding: 0.4rem 0.8rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  min-width: 50px;
  text-align: center;
`;

const QuantityLabel = styled.span`
  color: #7f8c8d;
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 480px;
  padding: 0 35px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 0 30px;
    max-width: 380px;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  gap: 0.5rem;
`;

const StaticProductContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: fit-content;
`;

const AdditionalProductsContainer = styled.div`
  flex: 1;
  overflow: hidden;
  max-width: 300px;
  width: 300px;
`;

const AdditionalProductsTrack = styled.div`
  display: flex;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  width: 100%;
`;

const ProductPage = styled.div`
  display: flex;
  gap: 0.5rem;
  min-width: 100%;
  flex-shrink: 0;
  justify-content: flex-start;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 45px;
  height: 45px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 107, 53, 0.9);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  z-index: 3;
  transition: all 0.3s ease;
  box-shadow: 0 3px 12px rgba(255, 107, 53, 0.4);

  &:hover {
    background: rgba(255, 107, 53, 1);
    transform: translateY(-50%) scale(1.15);
    box-shadow: 0 5px 20px rgba(255, 107, 53, 0.6);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: translateY(-50%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.left {
    left: -10px;
  }

  &.right {
    right: -10px;
  }
`;

const CarouselIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  order: 2;
`;

const CarouselDot = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border: none;
  border-radius: 50%;
  background: ${props => props.active ? '#FDBF37' : '#ddd'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? '#e55a2b' : '#bbb'};
  }
`;



const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToConsultation, itemCount } = useConsultation();
  const { isAuthenticated } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  // Misión 1: Revalidación inteligente con SWR (revalidateOnFocus por defecto)
  const { data: productData, error: productError, isLoading: loadingProduct } = useSWR(
    id ? `/products/${id}` : null,
    async () => {
      if (!id) return null;
      return await productService.getProduct(id);
    },
    {
      revalidateOnFocus: true, // SWR revalida silenciosamente al volver a la pestaña
    }
  );

  const product: Product | null = productData || null;

  // Carga de combos con SWR
  const { data: combosData, isLoading: loadingCombos } = useSWR(
    id ? `/combos/product/${id}` : null,
    async () => {
      if (!id) return [];
      try {
        const data = await comboService.getCombosByProduct(parseInt(id));
        return data || [];
      } catch (error: any) {
        if (error.response?.status !== 404) {
          console.error('Error al cargar combos:', error);
        }
        return [];
      }
    }
  );

  const combos: Combo[] = combosData || [];
  const loading = loadingProduct;

  // Efecto para seleccionar automáticamente todos los productos cuando se cargan los combos
  useEffect(() => {
    if (combos.length > 0 && product) {
      const allProducts = getAllComboProducts();
      const allProductIds = new Set(allProducts.map(p => Number(p.id)));
      setSelectedProducts(allProductIds);
    }
  }, [combos, product]);

  const handleAddToConsultation = () => {
    if (Number(product?.stock || 0) <= 0) {
      toast.error("Este producto está agotado");
      return;
    }

    if (!isAuthenticated) {
      toast.error("Debes iniciar sesión para agregar productos al carrito");
      navigate("/login");
      return;
    }

    if (product) {
      addToConsultation(product, quantity);
    }
  };

  const handleShare = () => {
    if (navigator.share && product) {
      navigator.share({
        title: product.name,
        text: product.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copiar URL al portapapeles
      navigator.clipboard.writeText(window.location.href);
      toast.success("URL copiada al portapapeles");
    }
  };

  const handleProductSelection = (productId: number, isSelected: boolean) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(productId);
      } else {
        newSet.delete(productId);
      }
      return newSet;
    });
  };

  const handleAddSelectedToCart = () => {
    if (selectedProducts.size === 0) {
      toast.warning("Selecciona al menos un producto para agregar a la consulta");
      return;
    }

    // Obtener todos los productos disponibles
    const allProducts = getAllComboProducts();
    
    // Filtrar productos seleccionados
    const productsToAdd = allProducts.filter(product => 
      selectedProducts.has(Number(product.id))
    );
    
    // Agregar productos a la consulta usando el contexto con la cantidad especificada
    productsToAdd.forEach(product => {
      for (let i = 0; i < quantity; i++) {
        addToConsultation(product);
      }
    });
    
    // Limpiar selección después de agregar
    setSelectedProducts(new Set());
    // Resetear cantidad a 1
    setQuantity(1);
  };

  const handleQuantityChange = (increment: boolean) => {
    if (increment) {
      setQuantity(prev => Math.min(prev + 1, 99)); // Máximo 99
    } else {
      setQuantity(prev => Math.max(prev - 1, 1)); // Mínimo 1
    }
  };

  const getVisibleProducts = (products: any[]) => {
    // Para el carrusel de combo, siempre mostrar el producto principal + productos adicionales según la página
    const additionalProducts = products.filter(p => p.id !== product?.id);
    
    if (currentCarouselIndex === 0) {
      // Primera página: producto principal + hasta 3 productos adicionales
      return [product, ...additionalProducts.slice(0, 3)];
    } else {
      // Páginas siguientes: producto principal + 3 productos adicionales siguientes
      const startIndex = 3 + (currentCarouselIndex - 1) * 3;
      return [product, ...additionalProducts.slice(startIndex, startIndex + 3)];
    }
  };

  const getTotalPages = (products: any[]) => {
    // Calcular páginas basado en productos adicionales (excluyendo el producto principal)
    const additionalProducts = products.filter(p => p.id !== product?.id);
    if (additionalProducts.length <= 3) return 1;
    // Cada página muestra 3 productos adicionales
    return Math.ceil(additionalProducts.length / 3);
  };

  const handleCarouselNavigation = (direction: 'prev' | 'next', products: any[]) => {
    const totalPages = getTotalPages(products);
    
    if (totalPages <= 1) return; // No navegar si solo hay una página
    
    if (direction === 'next' && currentCarouselIndex < totalPages - 1) {
      setCurrentCarouselIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentCarouselIndex > 0) {
      setCurrentCarouselIndex(prev => prev - 1);
    }
  };

  const goToCarouselPage = (pageIndex: number) => {
    setCurrentCarouselIndex(pageIndex);
  };

  const getAllComboProducts = () => {
    const allProducts: (Product | any)[] = [];
    
    combos.forEach(combo => {
      // Agregar el producto principal
      if (product) {
        allProducts.push(product);
      }
      
      // Agregar productos adicionales (ComboProduct tiene estructura diferente)
      const additionalProducts = combo.products?.filter(p => p.id.toString() !== product?.id.toString()) || [];
      allProducts.push(...additionalProducts);
    });
    
    // Eliminar duplicados basado en el ID
    const uniqueProducts = allProducts.filter((product, index, self) => 
      index === self.findIndex(p => p.id === product.id)
    );
    
    return uniqueProducts;
  };

  const handleOpenSidePanel = () => {
    setIsSidePanelOpen(true);
  };

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
  };

  const handleProductClick = (productId: number) => {
    handleCloseSidePanel();
    navigate(`/producto/${productId}`);
  };

  // Función para manejar la navegación de imágenes
  const handleImageNavigation = (direction: "prev" | "next") => {
    if (!product) return;
  
    const totalImages = 1 + (product.additional_images?.length || 0);
    
    // Referencia al elemento de imagen actual
    const imageElement = document.querySelector('.product-main-image') as HTMLImageElement;
    
    if (imageElement) {
      // Aplicar efecto de salida
      imageElement.style.opacity = '0';
      imageElement.style.transform = direction === "prev" ? 'translateX(20px)' : 'translateX(-20px)';
      
      // Cambiar la imagen después de la transición de salida
      setTimeout(() => {
        if (direction === "prev") {
          setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else {
          setCurrentImageIndex((prev) =>
            prev < totalImages - 1 ? prev + 1 : prev
          );
        }
        
        // Aplicar efecto de entrada después de cambiar la imagen
        setTimeout(() => {
          if (imageElement) {
            imageElement.style.opacity = '1';
            imageElement.style.transform = 'translateX(0)';
          }
        }, 50);
      }, 300);
    } else {
      // Fallback si no se encuentra el elemento
      if (direction === "prev") {
        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else {
        setCurrentImageIndex((prev) =>
          prev < totalImages - 1 ? prev + 1 : prev
        );
      }
    }
  };

  // Función para seleccionar una imagen específica
  const handleThumbnailClick = (index: number) => {
    // Referencia al elemento de imagen actual
    const imageElement = document.querySelector('.product-main-image') as HTMLImageElement;
    
    if (imageElement) {
      // Aplicar efecto de salida
      imageElement.style.opacity = '0';
      imageElement.style.transform = 'scale(0.95)';
      
      // Cambiar la imagen después de la transición de salida
      setTimeout(() => {
        setCurrentImageIndex(index);
        
        // Aplicar efecto de entrada después de cambiar la imagen
        setTimeout(() => {
          if (imageElement) {
            imageElement.style.opacity = '1';
            imageElement.style.transform = 'scale(1)';
          }
        }, 50);
      }, 300);
    } else {
      // Fallback si no se encuentra el elemento
      setCurrentImageIndex(index);
    }
  };

  // Obtener la URL de la imagen actual
  const getCurrentImageUrl = () => {
    if (!product) return "";

    if (currentImageIndex === 0) {
      return getImageUrl(product.image_url);
    } else {
      return getImageUrl(
        product.additional_images?.[currentImageIndex - 1] || ""
      );
    }
  };

  useEffect(() => {
    if (product) {
      // Agregar la categoría al historial cuando se carga el producto
      categoryHistory.add({
        id: String(product.category_id),
        name: product.category_name || '',
        image_url: getImageUrl(product.image_url || null)
      });
    }
  }, [product]);

  if (loading) {
    return (
      <IconContext.Provider value={{ className: "react-icons" }}>
        <Container>
          <LoadingContainer>Cargando producto...</LoadingContainer>
        </Container>
      </IconContext.Provider>
    );
  }

  if (!product) {
    return (
      <Container>
        <NotFound>
          <h2>Producto no encontrado</h2>
          <p>El producto que buscas no existe o ha sido eliminado.</p>
          <BackButton onClick={() => navigate("/products")}>
            <IconWrapper>{renderIcon(FiArrowLeft)}</IconWrapper> Volver a productos
          </BackButton>
        </NotFound>
      </Container>
    );
  }

  return (
    <Container>
      {/* Indicador de la consulta */}
       {itemCount > 0 && (
         <ConsultationIndicator onClick={() => navigate('/carrito')}>
           📋 {itemCount} producto{itemCount > 1 ? 's' : ''} en consulta
         </ConsultationIndicator>
       )}
      <BackButton onClick={() => navigate(-1)}>
        <IconWrapper>{renderIcon(FiArrowLeft)}</IconWrapper> Volver
      </BackButton>

      <ProductContainer>
        <ImageSection>
          <StickyImageWrapper>
            <GalleryContainer>
          <MainImageContainer>
            <ProductImage
              className="product-main-image" // Añadimos esta clase
              src={getCurrentImageUrl()}
              alt={product.name}
              onError={(e) => {
                if (e && e.target) {
                  (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                }
              }}
              style={{ opacity: 1, transform: 'translateX(0)' }} // Estilo inicial
            />
            {product.featured && <Badge type="featured">Destacado</Badge>}

            {/* Botones de navegación */}
            {(product.additional_images?.length || 0) > 0 && (
              <>
                <NavigationButton
                  className="prev"
                  onClick={() => handleImageNavigation("prev")}
                  disabled={currentImageIndex === 0}
                >
                  {renderIcon(FiArrowLeft, { style: { fontSize: "20px" } })}
                </NavigationButton>
                <NavigationButton
                  className="next"
                  onClick={() => handleImageNavigation("next")}
                  disabled={
                    currentImageIndex >=
                    1 + (product.additional_images?.length || 0) - 1
                  }
                >
                  {renderIcon(FiArrowRight, { style: { fontSize: "20px" } })}
                </NavigationButton>
              </>
            )}
          </MainImageContainer>

          {/* Miniaturas */}
          {(product.additional_images?.length || 0) > 0 && (
            <ThumbnailsContainer>
              <Thumbnail
                active={currentImageIndex === 0}
                onClick={() => handleThumbnailClick(0)}
              >
                <img
                  src={getImageUrl(product.image_url)}
                  alt="Miniatura principal"
                  onError={(e) => {
                    if (e && e.target) {
                      (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                    }
                  }}
                />
              </Thumbnail>

              {product.additional_images?.map((imgUrl, index) => (
                <Thumbnail
                  key={index}
                  active={currentImageIndex === index + 1}
                  onClick={() => handleThumbnailClick(index + 1)}
                >
                  <img
                    src={getImageUrl(imgUrl)}
                    alt={`Miniatura ${index + 1}`}
                    onError={(e) => {
                      if (e && e.target) {
                        (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                      }
                    }}
                  />
                </Thumbnail>
              ))}
            </ThumbnailsContainer>
          )}
            </GalleryContainer>
          </StickyImageWrapper>
        </ImageSection>

        <ProductInfo>
          <ProductCategory>
            {product.category_name || "Sin categoría"}
          </ProductCategory>
          <ProductName>{product.name}</ProductName>
          <ProductDescription>{product.description}</ProductDescription>

          {/* Precio Formateado Grande */}
          <PriceDisplay>
            {product.use_offer_price && Number(product.offer_price || 0) > 0 ? (
              <div style={{ display: "flex", alignItems: "baseline", gap: "1rem", flexWrap: "wrap" }}>
                <del style={{ color: "#94a3b8", fontSize: "1.4rem", fontWeight: 500 }}>
                  S/. {Number(product.price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </del>
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem" }}>
                  <span className="currency" style={{ color: "#ea580c" }}>S/.</span>
                  <span className="amount" style={{ color: "#ea580c", fontWeight: 800 }}>
                    {Number(product.offer_price).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <span className="currency">S/.</span>
                <span className="amount">
                  {Number(product.price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </>
            )}
          </PriceDisplay>

          {/* Controles de Cantidad */}
          <MainQuantitySection>
            <span style={{ fontWeight: 600, color: "#475569", fontSize: "0.95rem" }}>Cantidad:</span>
            <MainQuantityControls>
              <MainQuantityButton
                onClick={() => handleQuantityChange(false)}
                disabled={quantity <= 1 || Number(product.stock || 0) <= 0}
              >
                {renderIcon(FiMinus)}
              </MainQuantityButton>
              <MainQuantityDisplay>{quantity}</MainQuantityDisplay>
              <MainQuantityButton
                onClick={() => handleQuantityChange(true)}
                disabled={Number(product.stock || 0) <= 0 || quantity >= (product.stock || 99)}
              >
                {renderIcon(FiPlus)}
              </MainQuantityButton>
            </MainQuantityControls>
          </MainQuantitySection>

          <ActionButtons>
            {Number(product.stock || 0) > 0 ? (
              <MainAddToCartButton
                onClick={handleAddToConsultation}
              >
                <IconWrapper>{renderIcon(FiShoppingCart)}</IconWrapper> Agregar al carrito
              </MainAddToCartButton>
            ) : (
              <MainAddToCartButton
                isOutOfStock={true}
                disabled={true}
                onClick={(e) => e.stopPropagation()}
              >
                🚫 Agotado
              </MainAddToCartButton>
            )}

            <SecondaryButton onClick={handleShare} title="Compartir">
              <IconWrapper>{renderIcon(FiShare2)}</IconWrapper>
            </SecondaryButton>

            <SecondaryButton title="Favorito">
              <IconWrapper>{renderIcon(FiHeart)}</IconWrapper>
            </SecondaryButton>
          </ActionButtons>

          {/* Sección de Combos - Justo después del botón Consultar precio */}
          {!loadingCombos && combos.length > 0 && (
            <ComboDisplaySection>
              <ComboDisplayTitle>¡Completa tu compra!</ComboDisplayTitle>
              {combos.map((combo) => {
                // Filtrar productos adicionales excluyendo el producto principal
                const additionalProducts = combo.products?.filter(p => p.id.toString() !== product.id.toString()) || [];
                
                return (
                  <ComboDisplayCard key={combo.id}>
                    <ComboDisplayProducts>
                      <CarouselContainer>
                        {(() => {
                          const productsPerPage = 3;
                          const totalPages = Math.ceil(additionalProducts.length / productsPerPage);
                          return totalPages > 1 ? (
                            <CarouselButton 
                               className="left"
                               onClick={() => handleCarouselNavigation('prev', additionalProducts)}
                               disabled={currentCarouselIndex === 0}
                             >
                               {renderIcon(FiChevronLeft)}
                             </CarouselButton>
                          ) : null;
                        })()}
                        
                        <CarouselWrapper>
                          <CarouselTrack>
                            {/* Producto principal - siempre estático */}
                            <StaticProductContainer>
                              <ProductInCombo isMain={true}>
                                <ProductCheckbox
                                  type="checkbox"
                                  checked={selectedProducts.has(Number(product.id))}
                                  onChange={(e) => handleProductSelection(Number(product.id), e.target.checked)}
                                />
                                <ProductContent>
                                  <ComboProductImage src={getImageUrl(product.image_url)} alt={product.name} />
                                  <ComboProductName isMain={true}>
                                    {product.name.length > 30 ? `${product.name.substring(0, 30)}...` : product.name}
                                  </ComboProductName>
                                </ProductContent>
                              </ProductInCombo>
                              
                              {/* Solo mostrar símbolo + si hay productos adicionales */}
                              {additionalProducts.length > 0 && <PlusSymbol>+</PlusSymbol>}
                            </StaticProductContainer>
                            
                            {/* Contenedor para productos adicionales con animación */}
                            {additionalProducts.length > 0 && (
                              <AdditionalProductsContainer>
                                <AdditionalProductsTrack
                                  style={{
                                    transform: `translateX(-${currentCarouselIndex * 100}%)`,
                                  }}
                                >
                                  {(() => {
                                    const additionalProducts = getAllComboProducts().filter(p => p.id !== product?.id);
                                    const productsPerPage = 3; // Volver a 3 productos por página
                                    const totalPages = Math.ceil(additionalProducts.length / productsPerPage);
                                    
                                    return Array.from({ length: totalPages }, (_, pageIndex) => (
                                      <ProductPage key={pageIndex}>
                                        {additionalProducts
                                          .slice(pageIndex * productsPerPage, (pageIndex + 1) * productsPerPage)
                                          .map((comboProduct, index) => (
                                            <ProductInCombo key={`${combo.id}-${comboProduct.id}-${index}`} isMain={false}>
                                              <ProductCheckbox
                                                type="checkbox"
                                                checked={selectedProducts.has(Number(comboProduct.id))}
                                                onChange={(e) => handleProductSelection(Number(comboProduct.id), e.target.checked)}
                                              />
                                              <ProductContent>
                                                <ComboProductImage 
                                                  src={getImageUrl(comboProduct.image_url || '')} 
                                                  alt={comboProduct.name}
                                                  onError={(e) => {
                                                    if (e && e.target) {
                                                      (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                                                    }
                                                  }}
                                                />
                                                <ComboProductName>
                                                  {comboProduct.name.length > 30 ? `${comboProduct.name.substring(0, 30)}...` : comboProduct.name}
                                                </ComboProductName>
                                              </ProductContent>
                                            </ProductInCombo>
                                          ))
                                        }
                                        {/* Agregar el botón Ver listado al final de la última página */}
                                        {pageIndex === totalPages - 1 && (
                                          <ViewListContainer onClick={handleOpenSidePanel}>
                                            <ViewListIcon>
                                              <div className="e-letter">
                                                <div className="e-line"></div>
                                                <div className="e-line middle"></div>
                                                <div className="e-line"></div>
                                              </div>
                                            </ViewListIcon>
                                            <ViewListText>Ver listado</ViewListText>
                                          </ViewListContainer>
                                        )}
                                      </ProductPage>
                                    ));
                                  })()} 
                                </AdditionalProductsTrack>
                              </AdditionalProductsContainer>
                            )}
                            
                            {/* Si no hay productos adicionales, mostrar solo el botón Ver listado */}
                            {additionalProducts.length === 0 && (
                              <ViewListContainer onClick={handleOpenSidePanel}>
                                <ViewListIcon>
                                  <div className="e-letter">
                                    <div className="e-line"></div>
                                    <div className="e-line middle"></div>
                                    <div className="e-line"></div>
                                  </div>
                                </ViewListIcon>
                                <ViewListText>Ver listado</ViewListText>
                              </ViewListContainer>
                            )}
                          </CarouselTrack>
                        </CarouselWrapper>
                        
                        {(() => {
                           const productsPerPage = 3; // Volver a 3 productos por página
                           const totalPages = Math.ceil(additionalProducts.length / productsPerPage);
                           return totalPages > 1 ? (
                             <CarouselButton 
                                className="right"
                                onClick={() => handleCarouselNavigation('next', additionalProducts)}
                                disabled={currentCarouselIndex >= totalPages - 1}
                              >
                                {renderIcon(FiChevronRight)}
                              </CarouselButton>
                           ) : null;
                         })()}
                         

                      </CarouselContainer>
                      
                      {/* Indicadores del carrusel - solo mostrar si hay productos adicionales */}
                      {(() => {
                        const additionalProducts = getAllComboProducts().filter(p => p.id !== product?.id);
                        const productsPerPage = 3; // Corregido para coincidir con el carrusel
                        const totalPages = Math.ceil(additionalProducts.length / productsPerPage);
                        
                        return additionalProducts.length > 0 && totalPages > 1 ? (
                          <CarouselIndicators>
                            {Array.from({ length: totalPages }, (_, index) => (
                              <CarouselDot
                                key={index}
                                active={index === currentCarouselIndex}
                                onClick={() => setCurrentCarouselIndex(index)}
                              />
                            ))}
                          </CarouselIndicators>
                        ) : null;
                      })()}
                    </ComboDisplayProducts>
                    
                    <AddToCartSection>
                      <QuantitySelector>
                        
                        <QuantityButton 
                            onClick={() => handleQuantityChange(false)}
                            disabled={quantity <= 1}
                          >
                            {renderIcon(FiMinus)}
                          </QuantityButton>
                          <QuantityDisplay>{quantity}</QuantityDisplay>
                          <QuantityButton 
                            onClick={() => handleQuantityChange(true)}
                            disabled={quantity >= 99}
                          >
                            {renderIcon(FiPlus)}
                          </QuantityButton>
                      </QuantitySelector>
                      
                      {/* Botón para agregar productos seleccionados a la consulta */}
                      <AddToCartButton
                        onClick={handleAddSelectedToCart}
                        disabled={selectedProducts.size === 0}
                      >
                        <IconWrapper>{renderIcon(FiShoppingCart)}</IconWrapper>
                        Agregar todos los productos<br />seleccionados
                      </AddToCartButton>
                    </AddToCartSection>
                  </ComboDisplayCard>
                );
              })}
            </ComboDisplaySection>
          )}
        </ProductInfo>
      </ProductContainer>

      {/* Panel lateral */}
      <SidePanelOverlay isOpen={isSidePanelOpen} onClick={handleCloseSidePanel} />
      <SidePanel isOpen={isSidePanelOpen}>
        <SidePanelHeader>
          <SidePanelTitle>Productos del Combo</SidePanelTitle>
          <CloseButton onClick={handleCloseSidePanel}>
            {renderIcon(FiX)}
          </CloseButton>
        </SidePanelHeader>
        
        {getAllComboProducts().map((comboProduct, index) => (
          <ProductListItem 
            key={`${comboProduct.id}-${index}`}
            onClick={() => handleProductClick(comboProduct.id)}
          >
            <SidePanelCheckbox
               type="checkbox"
               checked={selectedProducts.has(Number(comboProduct.id))}
               onChange={(e) => {
                 e.stopPropagation();
                 handleProductSelection(Number(comboProduct.id), e.target.checked);
               }}
               onClick={(e) => e.stopPropagation()}
             />
            <ProductListImage 
              src={getImageUrl(comboProduct.image_url || '')} 
              alt={comboProduct.name}
              onError={(e) => {
                if (e && e.target) {
                  (e.target as HTMLImageElement).src = "/placeholder-image.jpg";
                }
              }}
            />
            <ProductListInfo>
              <ProductListName>{comboProduct.name}</ProductListName>
              <ProductListCategory>
                {comboProduct.category_name || "Sin categoría"}
              </ProductListCategory>
            </ProductListInfo>
          </ProductListItem>
        ))}
      </SidePanel>

    </Container>
  );
};

export default ProductDetail;
